/**
 * Idle sign-out for "remember me" unticked logins.
 *
 * Every tab writes when it was last used to localStorage, so the tabs share
 * one clock: working in any tab keeps them all signed in. After
 * AUTH_CONFIG.IDLE_TIMEOUT_MS without activity anywhere, the app warns for
 * the last AUTH_CONFIG.IDLE_WARNING_MS and then signs out. The timestamp
 * survives a browser restart, so a browser that brings back its tabs (and
 * session cookies) still signs out a login that sat idle.
 *
 * "Remember me" ticked: nothing is signed out (activity still renews an
 * expired access token, see below).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AUTH_CONFIG } from "@/constants";
import { TokenStorage, endSession, refreshSession } from "@/api/client";
import { idleState, recordActivity, type IdleSignOutReason } from "@/utils/idleSession";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;

interface UseIdleSignOutOptions {
  /** Signed in (and the auth store has hydrated). Does nothing otherwise. */
  enabled: boolean;
  /** Called once when the login has to end. */
  onSignOut: (reason: IdleSignOutReason) => void;
}

interface IdleSignOutState {
  /** The warning is showing. */
  warning: boolean;
  /** Seconds until sign-out while the warning shows. */
  secondsLeft: number;
  /** "Stay signed in": counts as activity and closes the warning. */
  staySignedIn: () => void;
}

export function useIdleSignOut({ enabled, onSignOut }: UseIdleSignOutOptions): IdleSignOutState {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const warningRef = useRef(false);
  const onSignOutRef = useRef(onSignOut);
  const signedOutRef = useRef(false);
  const lastWriteRef = useRef(0);
  const refreshingRef = useRef(false);
  const checkRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    onSignOutRef.current = onSignOut;
  }, [onSignOut]);

  const showWarning = useCallback((show: boolean) => {
    warningRef.current = show;
    setWarning(show);
  }, []);

  // The server renews a login only when the access token is refreshed, which
  // otherwise happens on the first API call after it expires. Someone reading
  // or typing without API calls would move only the UI's clock, and the
  // server's limit could end the login under them; so activity refreshes an
  // expired access token too.
  const renewIfExpired = useCallback(() => {
    if (refreshingRef.current || !TokenStorage.getAccessToken() || !TokenStorage.isTokenExpired()) {
      return;
    }
    refreshingRef.current = true;
    refreshSession()
      .then((result) => {
        if (result === "refused") endSession();
      })
      .finally(() => {
        refreshingRef.current = false;
      });
  }, []);

  useEffect(() => {
    if (!enabled) {
      signedOutRef.current = false;
      if (warningRef.current) showWarning(false);
      return;
    }

    let timer: number | undefined;
    const signOut = (reason: IdleSignOutReason) => {
      signedOutRef.current = true;
      showWarning(false);
      onSignOutRef.current(reason);
    };

    // Decide, then sleep until the next thing that can happen: the warning,
    // the countdown's next second, or the sign-out. A ticked login never
    // wakes; a storage change (another tab's login or activity) re-decides.
    const check = () => {
      window.clearTimeout(timer);
      timer = undefined;
      if (signedOutRef.current) return;
      const state = idleState();
      if (state.kind === "ticked") {
        if (warningRef.current) showWarning(false);
        return;
      }
      if (state.kind === "ended") {
        signOut("ended");
        return;
      }
      let idle = 0;
      if (state.kind === "legacy-open") {
        recordActivity(); // its clock starts now
      } else {
        idle = state.idleMs;
      }
      const warnAt = AUTH_CONFIG.IDLE_TIMEOUT_MS - AUTH_CONFIG.IDLE_WARNING_MS;
      let next: number;
      if (idle >= AUTH_CONFIG.IDLE_TIMEOUT_MS) {
        signOut("idle");
        return;
      } else if (idle >= warnAt) {
        if (!warningRef.current) showWarning(true);
        setSecondsLeft(Math.ceil((AUTH_CONFIG.IDLE_TIMEOUT_MS - idle) / 1000));
        next = AUTH_CONFIG.IDLE_CHECK_INTERVAL_MS;
      } else {
        // Someone kept working in another tab.
        if (warningRef.current) showWarning(false);
        next = Math.min(warnAt - idle, AUTH_CONFIG.IDLE_MAX_SLEEP_MS);
      }
      timer = window.setTimeout(check, next);
    };
    checkRef.current = check;

    const onActivity = () => {
      // While the warning shows, only "Stay signed in" counts: a stray
      // mouse movement by someone walking past must not keep a shared
      // computer signed in.
      if (warningRef.current) return;
      const now = Date.now();
      if (now - lastWriteRef.current < AUTH_CONFIG.ACTIVITY_WRITE_THROTTLE_MS) return;
      // Decide first: the first touch after a laptop wakes (before its timer
      // has fired) must not count as activity past the limit or the warning.
      check();
      if (signedOutRef.current || warningRef.current) return;
      lastWriteRef.current = now;
      recordActivity(now);
      renewIfExpired();
    };
    // Background tabs and sleeping laptops barely run timers.
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, { passive: true, capture: true });
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", check);
    check();

    return () => {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity, { capture: true });
      }
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", check);
      window.clearTimeout(timer);
      checkRef.current = () => undefined;
    };
  }, [enabled, showWarning, renewIfExpired]);

  const staySignedIn = useCallback(() => {
    const now = Date.now();
    lastWriteRef.current = now;
    recordActivity(now);
    showWarning(false);
    renewIfExpired();
    checkRef.current();
  }, [showWarning, renewIfExpired]);

  return { warning, secondsLeft, staySignedIn };
}
